import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {getLanguageForHostname, getPublicBaseUrl} from '../../environment';

function getLanguageFromDomain() {
    if (typeof window === 'undefined') return 'nl';
    return getLanguageForHostname(window.location.hostname) || 'nl';
}

/**
 * SEO Component for managing meta tags, hreflang, and canonical URLs
 */
export default function SEOHead({
    title,
    description,
    keywords,
    ogImage,
    ogImageAlt,
    noIndex = false,
    language
}) {
    const location = useLocation();
    const currentLanguage = language || getLanguageFromDomain();
    const currentDomain = getPublicBaseUrl(window.location.hostname, currentLanguage);
    const siteName = currentLanguage === 'en' ? 'Plucking' : 'Oogst.app';

    useEffect(() => {
        // Set document title
        document.title = title;

        // Update meta tags
        updateMetaTag('description', description);
        updateMetaTag('keywords', keywords);
        updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

        // Update language
        document.documentElement.lang = currentLanguage;

        // Open Graph meta tags
        updateMetaTag('og:title', title, 'property');
        updateMetaTag('og:description', description, 'property');
        updateMetaTag('og:url', `${currentDomain}${location.pathname}`, 'property');
        updateMetaTag('og:locale', getLocaleCode(currentLanguage), 'property');
        updateMetaTag('og:site_name', siteName, 'property');

        if (ogImage) {
            updateMetaTag('og:image', ogImage, 'property');
            updateMetaTag('og:image:alt', ogImageAlt || title, 'property');
        }

        // Twitter Card meta tags
        updateMetaTag('twitter:title', title, 'name');
        updateMetaTag('twitter:description', description, 'name');

        // Remove any existing hreflang and canonical links (not needed — pages are language-specific per domain)
        document.querySelectorAll('link[rel="alternate"], link[rel="canonical"]').forEach(el => el.remove());

    }, [title, description, keywords, currentLanguage, currentDomain, location.pathname, ogImage, ogImageAlt, noIndex, siteName]);

    return null; // This component doesn't render anything
}

// Helper function to update meta tags
function updateMetaTag(name, content, attribute = 'name') {
    if (!content) return;

    let element = document.querySelector(`meta[${attribute}="${name}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
}

// Helper function to get locale code for og:locale
function getLocaleCode(language) {
    const locales = {
        nl: 'nl_NL',
        de: 'de_DE',
        en: 'en_US',
        fr: 'fr_FR'
    };
    return locales[language] || 'nl_NL';
}
